/** Entry point for Rush Fever 7s Deluxe */

import { Application, Assets, Container, Sprite, Texture, Graphics, Text, TextStyle } from 'pixi.js';
import { GameConfig, SYMBOL_TEXTURE_MAP, SymbolId } from './config/GameConfig';
import { BASE_GAME_STRIPS, FREE_GAME_STRIPS } from './config/ReelStrips';
import { ReelSet } from './reels/ReelSet';
import { registerSymbolTexture } from './reels/ReelSymbol';
import { GameStateMachine, GameState } from './core/GameStateMachine';
import { BalanceManager } from './core/BalanceManager';
import { ClientSpinResultGenerator } from './core/SpinResultGenerator';
import { WinEvaluator } from './core/WinEvaluator';
import { WinLineAnimation } from './animations/WinLineAnimation';
import { ParticleEffects } from './animations/ParticleEffects';
import { AudioManager } from './audio/AudioManager';
import { Howler } from 'howler';
import { Logger } from './utils/Logger';
import { FreeGamesFeature } from './features/FreeGamesFeature';
import { RushFeverFeature } from './features/RushFeverFeature';
import { JackpotPickFeature } from './features/JackpotPickFeature';
import { BigWinPresentation, BigWinTier } from './features/BigWinPresentation';
import { FREE_GAME_REEL_COUNT } from './config/FeatureConfig';
import { SpineManager } from './animations/SpineManager';

// QA tools
import { QAController } from './qa/QAController';
import { QAPanel } from './qa/QAPanel';
import { QAStats } from './qa/QAStats';

// UI components
import { SpinButton, SpinButtonState } from './ui/SpinButton';
import { BetSelector } from './ui/BetSelector';
import { HUD } from './ui/HUD';
import { MenuButton } from './ui/MenuButton';
import { Paytable } from './ui/Paytable';
import { AutoplayPanel, AutoplayConfig } from './ui/AutoplayPanel';
import { ResponsiveLayout } from './ui/ResponsiveLayout';
import { BuyFeatureButton, BUY_FEATURE_COST_MULTIPLIER } from './ui/BuyFeatureButton';
import { TurboButton } from './ui/TurboButton';
import { JackpotPanel } from './ui/JackpotPanel';

const logger = new Logger('main');

/** Update the HTML progress bar */
function setProgress(pct: number): void {
  const bar = document.getElementById('progress-bar');
  if (bar) {
    bar.style.width = `${Math.round(pct * 100)}%`;
  }
}

/** Hide the HTML preloader overlay */
function hidePreloader(): void {
  const preloader = document.getElementById('preloader');
  if (preloader) {
    preloader.style.transition = 'opacity 0.4s';
    preloader.style.opacity = '0';
    setTimeout(() => {
      preloader.style.display = 'none';
    }, 400);
  }
}

async function init(): Promise<void> {
  logger.info('Initializing Rush Fever 7s Deluxe...');
  setProgress(0);

  // 1. Create PixiJS v8 application
  const app = new Application();

  await app.init({
    background: '#0a0a1a',
    resizeTo: window,
    antialias: true,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
  });

  const container = document.getElementById('game-container');
  if (!container) {
    throw new Error('Game container element not found');
  }
  container.appendChild(app.canvas);

  setProgress(0.05);

  // 2. Set up responsive layout manager
  const responsiveLayout = new ResponsiveLayout(app);
  logger.info('Responsive layout handler initialized');
  setProgress(0.1);

  // ====================================================================
  // ASSET LOADING: Bundle-based loading with critical/deferred split
  // ====================================================================

  // --- CRITICAL BUNDLE: symbols + background (needed before first frame) ---
  logger.info('Loading critical assets (symbols + background)...');
  const symbolAssets: Record<string, string> = {};
  for (const [id, path] of Object.entries(SYMBOL_TEXTURE_MAP)) {
    if (path && path.length > 0) {
      symbolAssets[`symbol_${id}`] = path;
    }
  }
  symbolAssets['bg_main'] = 'assets/backgrounds/bg_1920x1920.png';
  symbolAssets['bg_back'] = 'assets/backgrounds/back_1920x1920.png';
  symbolAssets['bg_reelframe'] = 'assets/backgrounds/reelframe_base.png';
  symbolAssets['bg_reelframe_fg'] = 'assets/backgrounds/reelframe_fg.png';

  // UI button assets — use PATH as key so components can Assets.load(path) and hit cache
  symbolAssets['assets/ui/frame_buttion_baf.png'] = 'assets/ui/frame_buttion_baf.png';
  symbolAssets['assets/ui/turbo_button_off_normal.png'] = 'assets/ui/turbo_button_off_normal.png';
  symbolAssets['assets/sprites/turbo_icon_off_normal_en.png'] = 'assets/sprites/turbo_icon_off_normal_en.png';
  symbolAssets['assets/ui/turbo_button_off_pressed.png'] = 'assets/ui/turbo_button_off_pressed.png';
  symbolAssets['assets/ui/turbo_button_on_normal.png'] = 'assets/ui/turbo_button_on_normal.png';
  symbolAssets['assets/sprites/turbo_icon_on_normal_en.png'] = 'assets/sprites/turbo_icon_on_normal_en.png';
  symbolAssets['assets/ui/turbo_button_on_pressed.png'] = 'assets/ui/turbo_button_on_pressed.png';
  symbolAssets['assets/ui/auto_button_normal.png'] = 'assets/ui/auto_button_normal.png';
  symbolAssets['assets/ui/auto_button_pressed.png'] = 'assets/ui/auto_button_pressed.png';
  symbolAssets['assets/ui/auto_button_disabled.png'] = 'assets/ui/auto_button_disabled.png';
  symbolAssets['assets/ui/auto_icon_normal_en.png'] = 'assets/ui/auto_icon_normal_en.png';
  symbolAssets['assets/ui/auto_icon_pressed_en.png'] = 'assets/ui/auto_icon_pressed_en.png';

  // Jackpot badge assets — use PATH as key for same reason
  symbolAssets['assets/sprites/jp_9.png'] = 'assets/sprites/jp_9.png';
  symbolAssets['assets/sprites/jp_8.png'] = 'assets/sprites/jp_8.png';
  symbolAssets['assets/sprites/jp_7.png'] = 'assets/sprites/jp_7.png';
  symbolAssets['assets/sprites/jp_6.png'] = 'assets/sprites/jp_6.png';

  Assets.addBundle('critical', symbolAssets);

  const criticalTextures = await Assets.loadBundle('critical', (progress) => {
    setProgress(0.1 + 0.45 * progress);
  });

  // Pre-load UI assets individually so component Assets.load(path) hits PixiJS cache.
  // The bundle may not register them by path — load them explicitly before components init.
  const uiAssetPaths = [
    'assets/ui/frame_buttion_baf.png',
    'assets/ui/turbo_button_off_normal.png', 'assets/sprites/turbo_icon_off_normal_en.png',
    'assets/ui/turbo_button_off_pressed.png', 'assets/ui/turbo_button_on_normal.png',
    'assets/sprites/turbo_icon_on_normal_en.png', 'assets/ui/turbo_button_on_pressed.png',
    'assets/ui/auto_button_normal.png', 'assets/ui/auto_button_pressed.png',
    'assets/ui/auto_button_disabled.png', 'assets/ui/auto_icon_normal_en.png',
    'assets/ui/auto_icon_pressed_en.png',
    'assets/sprites/jp_9.png', 'assets/sprites/jp_8.png',
    'assets/sprites/jp_7.png', 'assets/sprites/jp_6.png',
  ];
  await Promise.all(uiAssetPaths.map(p => Assets.load(p).catch(() => null)));
  logger.info(`Pre-loaded ${uiAssetPaths.length} UI assets by path`);

  // Register loaded symbol textures from bundle
  let loadedCount = 0;
  for (const [id, path] of Object.entries(SYMBOL_TEXTURE_MAP)) {
    if (path && path.length > 0) {
      const key = `symbol_${id}`;
      const texture = criticalTextures[key] as Texture | undefined;
      if (texture) {
        registerSymbolTexture(id, texture);
        loadedCount++;
      } else {
        logger.warn(`Texture not found in bundle for ${id}`);
      }
    }
  }
  logger.info(`Loaded ${loadedCount} symbol textures via bundle`);

  const bgMainTexture: Texture | null = (criticalTextures['bg_main'] as Texture) ?? null;
  const bgBackTexture: Texture | null = (criticalTextures['bg_back'] as Texture) ?? null;
  const bgReelframeTexture: Texture | null = (criticalTextures['bg_reelframe'] as Texture) ?? null;
  const bgReelframeFgTexture: Texture | null = (criticalTextures['bg_reelframe_fg'] as Texture) ?? null;
  if (bgMainTexture) {
    logger.info('Main background texture loaded');
  } else {
    logger.warn('Main background texture not found in bundle');
  }
  if (bgReelframeTexture) {
    logger.info('Reel frame texture loaded');
  } else {
    logger.warn('Reel frame texture not found in bundle');
  }
  setProgress(0.6);

  // --- AUDIO: Load essential SFX first, defer feature audio ---
  logger.info('Loading essential audio...');
  const audioManager = AudioManager.getInstance();
  try {
    await audioManager.preloadAll();
  } catch (err) {
    logger.warn(`Audio preload encountered issues: ${err}`);
  }
  setProgress(0.9);

  // --- DEFERRED BUNDLE: feature-specific assets loaded in background ---
  // These load after the game is playable (Spine skeletons, particle textures, logo)
  const deferredAssets: Record<string, string> = {};
  deferredAssets['logo'] = 'assets/ui/Logo_oneline_en.png';

  // Add coin particle frames if they exist
  for (let i = 0; i < 20; i++) {
    const idx = String(i).padStart(5, '0');
    deferredAssets[`coin_${idx}`] = `assets/effects/coin_particle_${idx}.png`;
  }
  Assets.addBundle('deferred', deferredAssets);

  // Start loading deferred assets in background (non-blocking)
  const deferredPromise = Assets.loadBundle('deferred').catch((err) => {
    logger.warn(`Deferred asset load issues (non-critical): ${err}`);
    return null;
  });
  logger.info('Deferred asset bundle loading started in background');

  // Preload Spine skeletons in background (non-blocking, deferred)
  SpineManager.getInstance().preloadAll().then(() => {
    logger.info('Spine skeletons preloaded (deferred)');
  }).catch((err) => {
    logger.warn(`Spine preload encountered issues (non-critical): ${err}`);
  });

  // 5b. Start main game music on first user interaction (autoplay policy)
  // Must await ctx.resume() — calling play() while context is still suspended is a no-op.
  const startMusicOnInteraction = async () => {
    document.removeEventListener('pointerdown', startMusicOnInteraction);
    document.removeEventListener('keydown', startMusicOnInteraction);
    document.removeEventListener('touchstart', startMusicOnInteraction);
    try {
      if (Howler.ctx && Howler.ctx.state === 'suspended') {
        await Howler.ctx.resume();
      }
    } catch (e) {
      logger.warn(`AudioContext resume failed: ${e}`);
    }
    audioManager.playMusic('music_main');
    logger.info('Main game music started after user interaction');
  };
  document.addEventListener('pointerdown', startMusicOnInteraction, { once: true });
  document.addEventListener('keydown', startMusicOnInteraction, { once: true });
  document.addEventListener('touchstart', startMusicOnInteraction, { once: true });

  // Resume AudioContext when tab regains focus (browser may suspend it)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (Howler.ctx && Howler.ctx.state === 'suspended') {
        Howler.ctx.resume().then(() => {
          logger.info('AudioContext resumed after visibility change');
        }).catch(() => {});
      }
    }
  });

  // 6. Set up game systems
  const stateMachine = new GameStateMachine();
  const balanceManager = new BalanceManager();
  const spinGenerator = new ClientSpinResultGenerator();
  const winLineAnimation = new WinLineAnimation();
  const freeGamesFeature = new FreeGamesFeature(spinGenerator);
  const rushFeverFeature = new RushFeverFeature();
  const jackpotPickFeature = new JackpotPickFeature();
  const bigWinPresentation = new BigWinPresentation();
  const particleEffects = new ParticleEffects();
  particleEffects.preallocatePools();

  // 7. Get design dimensions
  const { innerWidth: ww, innerHeight: wh } = window;
  const isLandscapeInit = ww >= wh;
  let designW = isLandscapeInit
    ? GameConfig.DESIGN_WIDTH_LANDSCAPE
    : GameConfig.DESIGN_WIDTH_PORTRAIT;
  let designH = isLandscapeInit
    ? GameConfig.DESIGN_HEIGHT_LANDSCAPE
    : GameConfig.DESIGN_HEIGHT_PORTRAIT;

  // Root container for all game elements (positioned in design coordinates)
  const gameRoot = new Container();
  app.stage.addChild(gameRoot);

  // Full-screen background using back_1920x1920.png (the correct main bg)
  if (bgBackTexture) {
    const bgSprite = new Sprite(bgBackTexture);
    bgSprite.anchor.set(0.5, 0.5);
    bgSprite.x = designW / 2;
    bgSprite.y = designH / 2;
    const bgScale = Math.max(
      designW / bgBackTexture.width,
      designH / bgBackTexture.height,
    );
    bgSprite.scale.set(bgScale);
    gameRoot.addChild(bgSprite);
    logger.info('Main background (back_1920x1920) added');
  } else {
    const bg = new Graphics();
    bg.rect(0, 0, designW, designH);
    bg.fill({ color: 0x0a0a2e });
    gameRoot.addChild(bg);
  }

  // --- Diamond Pile Spine Animation (loaded here, positioned + inserted after reelSet is created) ---
  let pileDiamondSpine: import('@esotericsoftware/spine-pixi-v8').Spine | null = null;
  try {
    const spineMgr = SpineManager.getInstance();
    const pileLoaded = await spineMgr.loadSkeleton('pile_diamond');
    if (pileLoaded) {
      pileDiamondSpine = spineMgr.createInstance('pile_diamond');
      if (pileDiamondSpine) {
        logger.info('Diamond pile Spine instance created, will position after reelSet');
      } else {
        logger.warn('Diamond pile: createInstance returned null despite skeleton being loaded');
      }
    } else {
      logger.warn('Diamond pile: loadSkeleton returned false — skeleton failed to load');
    }
  } catch (err) {
    logger.warn(`Failed to load diamond pile Spine skeleton: ${err}`);
  }

  // Create reel set
  const reelSet = new ReelSet();
  const reelTotalW = reelSet.getTotalWidth();
  const reelTotalH = reelSet.getTotalHeight();

  // Center reels on screen
  reelSet.x = (designW - reelTotalW) / 2;
  reelSet.y = (designH - reelTotalH) / 2 - 60;
  gameRoot.addChild(reelSet);

  // Bind win animation to reel set
  winLineAnimation.setReelSet(reelSet);

  // Semi-transparent dark panel behind reels
  const reelBg = new Graphics();
  reelBg.roundRect(
    reelSet.x - 20,
    reelSet.y - 15,
    reelTotalW + 40,
    reelTotalH + 30,
    16,
  );
  reelBg.fill({ color: 0x000000, alpha: 0.5 });
  gameRoot.addChildAt(reelBg, gameRoot.children.indexOf(reelSet));

  // Reel frame chrome — positioned to wrap the 5-reel grid (landscape only)
  let reelFrameSprite: Sprite | null = null;
  if (bgReelframeTexture) {
    reelFrameSprite = new Sprite(bgReelframeTexture);
    reelFrameSprite.anchor.set(0.5, 0.5);
    // Position frame centered on the reel grid
    reelFrameSprite.x = reelSet.x + reelTotalW / 2;
    reelFrameSprite.y = reelSet.y + reelTotalH / 2;
    // Scale the frame to wrap the 5-reel grid with some padding
    const framePadX = 80; // Chrome frame extends beyond the reels
    const framePadY = 80;
    const frameScaleX = (reelTotalW + framePadX * 2) / bgReelframeTexture.width;
    const frameScaleY = (reelTotalH + framePadY * 2) / bgReelframeTexture.height;
    const frameScale = Math.max(frameScaleX, frameScaleY);
    reelFrameSprite.scale.set(frameScale);
    // Insert behind reels but in front of reelBg
    gameRoot.addChildAt(reelFrameSprite, gameRoot.children.indexOf(reelSet));
    logger.info('Reel frame chrome sprite added');
  }

  // Reel frame foreground overlay — FREE SPINS red frame, NOT shown in base game.
  // Texture is loaded (bgReelframeFgTexture) for future free spins mode but not added to scene.
  // let reelFrameFgSprite: Sprite | null = null;

  // --- Diamond Pile: position and insert into scene now that reelSet is created ---
  if (pileDiamondSpine) {
    // The diamond feature textures in the atlas are full-color crystals (cyan, blue, purple).
    // Use their original blend modes from the skeleton — no overrides needed.

    // Hide the logo slots embedded in the pile skeleton — we use a separate logo element
    const skeleton = pileDiamondSpine.skeleton;
    for (const slot of skeleton.slots) {
      if (slot.data.name.includes('Logo_oneline')) {
        slot.attachment = null;
      }
    }

    // Scale and position: the skeleton is ~1747×1347 native pixels.
    // Scale 0.5 gives ~874×674 in design coords, fitting nicely above the reels.
    const pileScale = 0.5;
    pileDiamondSpine.scale.set(pileScale);
    pileDiamondSpine.x = designW / 2;
    // Position so the pile's diamond area sits above the reel frame,
    // with bottom portion overlapping slightly behind the chrome.
    pileDiamondSpine.y = reelSet.y - (1347 * pileScale) / 2 + 120;

    // Layer order: background → reelBg → reelFrame → diamond pile → reelSet
    // Pile renders ON TOP of the chrome frame, with reels in front of the pile base
    const reelSetIndex = gameRoot.children.indexOf(reelSet);
    gameRoot.addChildAt(pileDiamondSpine, reelSetIndex);

    // Play the full diamond pile animation (lvl3 = maximum sparkle)
    const spineMgr = SpineManager.getInstance();
    spineMgr.playAnimation(pileDiamondSpine, 'idle_lvl3', true);
    logger.info(`Diamond pile inserted at index ${reelSetIndex}, pos=(${pileDiamondSpine.x}, ${pileDiamondSpine.y}), scale=${pileScale}`);
  } else {
    logger.warn('Diamond pile Spine not available — skipping');
  }

  // DEBUG: expose for console inspection (remove before release)
  (window as any).__pileDiamond__ = pileDiamondSpine;
  (window as any).__app__ = app;
  (window as any).__gameRoot__ = gameRoot;
  (window as any).__reelSet__ = reelSet;

  // 8. Create UI Components

  // --- HUD (Balance, Bet, Win displays) ---
  const hud = new HUD(reelTotalW + 40);
  hud.x = reelSet.x - 20;
  hud.y = reelSet.y + reelTotalH + 25;
  hud.setBalance(balanceManager.balance);
  hud.setBet(balanceManager.totalBet);
  gameRoot.addChild(hud);

  // Bind win display text to animation system
  winLineAnimation.setWinDisplay(gameRoot, hud.winDetailText);

  // --- Spin Button ---
  const spinButton = new SpinButton();
  spinButton.x = designW / 2 + reelTotalW / 2 + 100;
  spinButton.y = reelSet.y + reelTotalH / 2;
  gameRoot.addChild(spinButton);

  // --- Bet Selector (hidden by default — accessed via long-press on spin button) ---
  const betSelector = new BetSelector();
  betSelector.x = designW / 2 - reelTotalW / 2 - 100;
  betSelector.y = reelSet.y + reelTotalH / 2;
  betSelector.updateDisplay(balanceManager.currentBetIndex);
  gameRoot.addChild(betSelector);
  betSelector.visible = false;

  // --- Autoplay Panel ---
  const autoplayPanel = new AutoplayPanel();
  autoplayPanel.x = designW / 2 + reelTotalW / 2 + 100;
  autoplayPanel.y = reelSet.y + reelTotalH / 2 + 100;
  gameRoot.addChild(autoplayPanel);

  // --- Buy Feature Button ---
  const buyFeatureButton = new BuyFeatureButton();
  buyFeatureButton.x = designW / 2 - reelTotalW / 2 - 100;
  buyFeatureButton.y = reelSet.y + reelTotalH / 2 + 100;
  buyFeatureButton.updateCost(balanceManager.totalBet);
  gameRoot.addChild(buyFeatureButton);

  // --- Chevron ">" Button (left of Buy Feature) ---
  const chevronBtn = new Container();
  const chevronBg = new Graphics();
  chevronBg.roundRect(-20, -25, 40, 50, 6);
  chevronBg.fill({ color: 0x1a2a4a, alpha: 0.9 });
  chevronBg.stroke({ color: 0x4488ff, width: 2 });
  chevronBtn.addChild(chevronBg);
  const chevronArrow = new Graphics();
  chevronArrow.moveTo(-8, -12);
  chevronArrow.lineTo(8, 0);
  chevronArrow.lineTo(-8, 12);
  chevronArrow.stroke({ color: 0x4488ff, width: 3 });
  chevronBtn.addChild(chevronArrow);
  chevronBtn.eventMode = 'static';
  chevronBtn.cursor = 'pointer';
  gameRoot.addChild(chevronBtn);

  // --- Jackpot Panel ---
  const jackpotPanel = new JackpotPanel();
  gameRoot.addChild(jackpotPanel);

  // --- Menu Button (added after jackpotPanel so its overlay renders on top) ---
  const menuButton = new MenuButton(designW, designH);
  menuButton.x = designW - 50;
  menuButton.y = 40;
  gameRoot.addChild(menuButton);

  // --- Paytable Overlay ---
  const paytable = new Paytable(designW, designH);
  gameRoot.addChild(paytable);

  // --- Logo (loaded from deferred bundle, wait for it now) ---
  let logoSprite: Sprite | null = null;
  const deferredResult = await deferredPromise;
  if (deferredResult && deferredResult['logo']) {
    try {
      const logoTex = deferredResult['logo'] as Texture;
      logoSprite = new Sprite(logoTex);
      logoSprite.anchor.set(0.5, 1);
      logoSprite.x = designW / 2;
      logoSprite.y = reelSet.y - 15;
      const maxLogoWidth = reelTotalW * 0.7;
      if (logoSprite.width > maxLogoWidth) {
        const logoScale = maxLogoWidth / logoSprite.width;
        logoSprite.scale.set(logoScale);
      }
      gameRoot.addChild(logoSprite);
      logger.info('Logo loaded and displayed from deferred bundle');
    } catch (err) {
      logger.warn(`Failed to create logo sprite: ${err}`);
    }
  } else {
    logger.warn('Logo not available from deferred bundle');
  }

  // Title text (fallback if no logo)
  let titleText: Text | null = null;
  if (!logoSprite) {
    titleText = new Text({
      text: 'RUSH FEVER 7s DELUXE',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 42,
        fill: 0xf7b733,
        stroke: { color: 0x000000, width: 4 },
        dropShadow: {
          color: 0x000000,
          blur: 6,
          distance: 3,
          angle: Math.PI / 4,
        },
      }),
    });
    titleText.anchor.set(0.5, 1);
    titleText.x = designW / 2;
    titleText.y = reelSet.y - 25;
    gameRoot.addChild(titleText);
  }

  // --- Free Spin Counter UI ---
  const freeSpinCounterText = new Text({
    text: '',
    style: new TextStyle({
      fontFamily: 'Arial Black, Arial',
      fontSize: 36,
      fill: 0x00ffcc,
      stroke: { color: 0x000000, width: 4 },
      dropShadow: {
        color: 0x000000,
        blur: 4,
        distance: 2,
        angle: Math.PI / 4,
      },
    }),
  });
  freeSpinCounterText.anchor.set(0.5, 0.5);
  freeSpinCounterText.x = designW / 2;
  freeSpinCounterText.y = reelSet.y - 10;
  freeSpinCounterText.visible = false;
  gameRoot.addChild(freeSpinCounterText);

  /** Show or hide the free spin counter overlay */
  const updateFreeSpinCounter = (current: number, total: number, visible: boolean) => {
    freeSpinCounterText.visible = visible;
    if (visible) {
      freeSpinCounterText.text = `FREE SPINS: ${current}/${total}`;
    }
  };

  // --- FIX B: Status message bar (between HUD and spin button) ---
  const statusMessageText = new Text({
    text: 'PRESS SPIN TO START GAME',
    style: new TextStyle({
      fontFamily: 'Arial Black, Arial',
      fontSize: 30,
      fill: 0xffcc00,
      fontWeight: 'bold',
      stroke: { color: 0x000000, width: 4 },
    }),
  });
  statusMessageText.anchor.set(1.0, 0.5);
  statusMessageText.x = designW / 2;
  statusMessageText.y = hud.y + 100;
  gameRoot.addChild(statusMessageText);

  // --- "HOLD FOR SETTINGS" text curved along bottom arc of spin button ---
  const holdForSettingsContainer = new Container();
  {
    const holdText = 'HOLD FOR SETTINGS';
    const arcRadius = 75; // will be rescaled by ResponsiveLayout
    const totalArcAngle = 1.2; // radians (~69 degrees)
    const startAngle = Math.PI / 2 - totalArcAngle / 2; // centered at bottom

    const holdStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 11,
      fill: 0xaaaaaa,
      fontWeight: 'bold',
      letterSpacing: 0,
    });

    for (let i = 0; i < holdText.length; i++) {
      const t = i / (holdText.length - 1);
      const angle = startAngle + totalArcAngle * t;
      const charText = new Text({ text: holdText[i], style: holdStyle });
      charText.anchor.set(0.5, 0.5);
      charText.x = Math.cos(angle) * arcRadius;
      charText.y = Math.sin(angle) * arcRadius;
      charText.rotation = angle - Math.PI / 2;
      holdForSettingsContainer.addChild(charText);
    }
  }
  holdForSettingsContainer.x = designW / 2;
  holdForSettingsContainer.y = spinButton.y;
  gameRoot.addChild(holdForSettingsContainer);

  // Turbo toggle using sprite assets — standalone element positioned by layout
  const turboButton = new TurboButton();
  turboButton.x = designW * 0.68;
  turboButton.y = designH * 0.78;
  gameRoot.addChild(turboButton);

  // 9. Set up responsive layout with all elements
  responsiveLayout.setElements(
    {
      gameRoot,
      reelSet,
      reelBg,
      reelFrame: reelFrameSprite || undefined,
      hud,
      spinButton,
      betSelector,
      menuButton,
      autoplayPanel,
      paytable,
      buyFeatureButton,
      chevronBtn,
      turboButton,
      logo: logoSprite || undefined,
      title: titleText || undefined,
      statusMessage: statusMessageText,
      holdForSettings: holdForSettingsContainer,
      jackpotPanel,
      pileDiamond: pileDiamondSpine || undefined,
    },
    reelTotalW,
    reelTotalH,
  );

  // 9b. Keep designW/designH in sync with current orientation
  responsiveLayout.onResize((newW, newH) => {
    designW = newW;
    designH = newH;
  });

  // 9c. QA Controller — debug overlay (only active with ?qa=1)
  const qaController = QAController.getInstance();
  const qaStats = new QAStats();
  let qaPanel: QAPanel | null = null;
  if (QAController.isEnabled()) {
    qaController.parseURLParams();
    qaPanel = new QAPanel(qaController, qaStats);
    gameRoot.addChild(qaPanel.container);
    qaController.onSpin((result) => qaStats.recordSpin(result));
    // Apply balance override if set via URL
    const fb = qaController.getForceBalance();
    if (fb !== undefined) balanceManager.set(fb);
  }

  // 10. Wire up balance change listener
  balanceManager.onChange((data) => {
    hud.setBalance(data.balance);
    hud.setBet(data.totalBet);

    // Update buy feature button cost display
    buyFeatureButton.updateCost(data.totalBet);

    // Update spin button state based on affordability
    if (stateMachine.isIdle) {
      if (!balanceManager.canAffordBet()) {
        spinButton.setState(SpinButtonState.DISABLED);
      } else {
        spinButton.setState(SpinButtonState.IDLE);
      }

      // Update buy feature button enabled state
      const buyCost = data.totalBet * BUY_FEATURE_COST_MULTIPLIER;
      buyFeatureButton.setEnabled(data.balance >= buyCost);
    }
  });

  // 11. Wire up bet selector
  betSelector.onBetChange((direction) => {
    switch (direction) {
      case 'up':
        balanceManager.incrementBet();
        break;
      case 'down':
        balanceManager.decrementBet();
        break;
      case 'max':
        balanceManager.maxBet();
        break;
    }
    betSelector.updateDisplay(balanceManager.currentBetIndex);
  });

  // 12. Wire up menu button
  menuButton.onOpenPaytable(() => {
    paytable.show();
  });

  paytable.setOnClose(() => {
    logger.debug('Paytable closed');
  });

  // 13. Turbo mode state + UI toggle button
  let turboMode = false;

  turboButton.onChange((isOn) => {
    turboMode = isOn;
    spinButton.toggleTurbo();
    logger.info(`Turbo mode toggled: ${turboMode}`);
  });

  // 14. Spin flow
  let spinInProgress = false;
  let autoplayConfig: AutoplayConfig | null = null;
  let autoplayRemaining = 0;

  /**
   * Present the Rush Fever feature: show overlay text, calculate & award prize.
   * Returns the prize amount awarded.
   */
  const presentRushFever = async (
    rushCount: number,
    totalBet: number,
    isFreeGame: boolean,
  ): Promise<number> => {
    logger.info(`RUSH FEVER TRIGGERED! ${rushCount} Rush symbols, freeGame=${isFreeGame}`);

    // Calculate prize
    const prize = await rushFeverFeature.start(rushCount, totalBet, isFreeGame);
    const level = rushFeverFeature.getFeatureLevel(rushCount);

    // --- Visual presentation overlay ---
    const rushOverlay = new Container();
    rushOverlay.zIndex = 1000;
    gameRoot.addChild(rushOverlay);

    // Dimmed background
    const dimBg = new Graphics();
    dimBg.rect(0, 0, designW, designH);
    dimBg.fill({ color: 0x000000, alpha: 0.55 });
    rushOverlay.addChild(dimBg);

    // "RUSH FEVER!" banner
    const bannerText = new Text({
      text: 'RUSH FEVER!',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 64,
        fill: 0xff4444,
        stroke: { color: 0x000000, width: 6 },
        dropShadow: {
          color: 0x000000,
          blur: 8,
          distance: 4,
          angle: Math.PI / 4,
        },
      }),
    });
    bannerText.anchor.set(0.5, 0.5);
    bannerText.x = designW / 2;
    bannerText.y = designH / 2 - 80;
    rushOverlay.addChild(bannerText);

    // Prize calculation text
    const multiplier = rushFeverFeature.state.prizeMultiplier;
    let calcStr = `${rushCount} Rush x ${multiplier}x x $${totalBet.toFixed(2)}`;
    if (isFreeGame) {
      calcStr += ' x2 (Free Game)';
    }
    const calcText = new Text({
      text: calcStr,
      style: new TextStyle({
        fontFamily: 'Arial, Helvetica',
        fontSize: 28,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    calcText.anchor.set(0.5, 0.5);
    calcText.x = designW / 2;
    calcText.y = designH / 2;
    rushOverlay.addChild(calcText);

    // Prize amount text
    const prizeText = new Text({
      text: `WIN $${prize.toFixed(2)}`,
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 48,
        fill: 0xf7b733,
        stroke: { color: 0x000000, width: 5 },
        dropShadow: {
          color: 0x000000,
          blur: 6,
          distance: 3,
          angle: Math.PI / 4,
        },
      }),
    });
    prizeText.anchor.set(0.5, 0.5);
    prizeText.x = designW / 2;
    prizeText.y = designH / 2 + 70;
    rushOverlay.addChild(prizeText);

    // Feature level indicator
    const levelText = new Text({
      text: `Level ${level}`,
      style: new TextStyle({
        fontFamily: 'Arial, Helvetica',
        fontSize: 22,
        fill: 0x00ffcc,
      }),
    });
    levelText.anchor.set(0.5, 0.5);
    levelText.x = designW / 2;
    levelText.y = designH / 2 + 120;
    rushOverlay.addChild(levelText);

    // Award prize
    if (prize > 0) {
      balanceManager.addWin(prize);
    }

    // Hold overlay for presentation
    await delay(2500);

    // Cleanup
    gameRoot.removeChild(rushOverlay);
    rushOverlay.destroy({ children: true });
    rushFeverFeature.end();

    return prize;
  };

  const doSpin = async () => {
    if (spinInProgress) return;
    if (!stateMachine.isIdle) return;

    // If win presentation is running, skip it
    if (winLineAnimation.isPresenting) {
      winLineAnimation.skip();
      return;
    }

    // Check balance
    if (!balanceManager.canAffordBet()) {
      logger.warn('Insufficient balance');
      hud.setWinLabel('INSUFFICIENT BALANCE');
      spinButton.setState(SpinButtonState.DISABLED);
      // Stop autoplay if running
      if (autoplayConfig) {
        stopAutoplay();
      }
      return;
    }

    spinInProgress = true;
    hud.clearWin();
    statusMessageText.text = 'GOOD LUCK!';

    // === UI STATE: SPINNING ===
    spinButton.setState(SpinButtonState.SPINNING);
    betSelector.setEnabled(false);
    buyFeatureButton.setEnabled(false);

    // === STATE: SPINNING ===
    stateMachine.transition(GameState.SPINNING);
    stateMachine.events.emit('spin:start', undefined as unknown as void);

    // Deduct bet
    const totalBet = balanceManager.totalBet;
    balanceManager.deductBet();

    // Generate stop positions
    const strips = BASE_GAME_STRIPS;
    const spinResult = spinGenerator.generate(strips, {
      isBaseGame: true,
      totalBet,
    });

    logger.info(`Spin stop positions: [${spinResult.stopPositions.join(', ')}]`);

    // Start spinning all reels
    reelSet.startSpin();

    // Stop reels — turbo mode uses faster delays
    await reelSet.stopReels(spinResult.stopPositions, turboMode);

    // Get the resulting grid (full grid including hidden reel 6 for math evaluation)
    let grid = reelSet.getFullGrid();

    // QA: Override grid if forced
    if (QAController.isEnabled() && qaController.hasGridOverride()) {
      const forcedGrid = qaController.consumeGridOverride();
      if (forcedGrid) {
        grid = forcedGrid;
      }
    }

    logger.info('Grid result:');
    for (let row = 0; row < GameConfig.VISIBLE_ROWS; row++) {
      const rowSymbols = grid.map((reel) => reel[row]);
      logger.info(`  Row ${row}: ${rowSymbols.join(' | ')}`);
    }

    // === STATE: EVALUATING ===
    stateMachine.transition(GameState.EVALUATING);

    const wins = WinEvaluator.evaluate(grid, {
      isBaseGame: true,
      totalBet,
    });
    const totalWin = WinEvaluator.calculateTotalWin(wins, totalBet);
    const scatterCount = WinEvaluator.countScatters(grid);
    const rushCount = WinEvaluator.countRush(grid);

    logger.info(
      `Evaluation: ${wins.length} wins, totalWin=$${totalWin.toFixed(4)}, ` +
      `scatters=${scatterCount}, rush=${rushCount}`,
    );

    // === (1) PRESENT LINE WINS ===
    if (wins.length > 0 && totalWin > 0) {
      stateMachine.transition(GameState.PRESENTING_WINS);

      // Add line win to balance
      balanceManager.addWin(totalWin);

      // Play win sound based on magnitude
      const winMultiple = totalWin / totalBet;
      if (winMultiple >= GameConfig.BIG_WIN_AMAZING) {
        audioManager.playBigWinSound();
      } else {
        audioManager.playWinSound();
      }

      // Show win amount in HUD
      if (winMultiple >= GameConfig.BIG_WIN_INCREDIBLE) {
        hud.setWinLabel(`INCREDIBLE WIN: $${totalWin.toFixed(2)}`);
        logger.info(`INCREDIBLE WIN! ${winMultiple.toFixed(1)}x total bet`);
      } else if (winMultiple >= GameConfig.BIG_WIN_AWESOME) {
        hud.setWinLabel(`AWESOME WIN: $${totalWin.toFixed(2)}`);
        logger.info(`AWESOME WIN! ${winMultiple.toFixed(1)}x total bet`);
      } else if (winMultiple >= GameConfig.BIG_WIN_AMAZING) {
        hud.setWinLabel(`AMAZING WIN: $${totalWin.toFixed(2)}`);
        logger.info(`AMAZING WIN! ${winMultiple.toFixed(1)}x total bet`);
      } else {
        hud.setWin(totalWin);
      }

      // Sparkle on each winning symbol position
      for (const win of wins) {
        for (const [reelIdx, row] of win.positions) {
          const symX = reelSet.x + reelIdx * GameConfig.SYMBOL_WIDTH + GameConfig.SYMBOL_WIDTH / 2;
          const symY = reelSet.y + row * GameConfig.SYMBOL_HEIGHT + GameConfig.SYMBOL_HEIGHT / 2;
          particleEffects.sparkleAt(gameRoot, symX, symY);
        }
      }

      // Cycle through win highlights
      await winLineAnimation.presentWins(wins, totalBet);

      // Check for Big Win celebration
      const bigWinTier = BigWinPresentation.getTier(totalWin, totalBet);
      if (bigWinTier !== BigWinTier.NONE) {
        logger.info(`Big Win detected: tier=${bigWinTier}, ${(totalWin / totalBet).toFixed(1)}x bet`);
        // Launch coin shower alongside the big win presentation
        const coinShowerPromise = particleEffects.coinShower(gameRoot, designW, designH);
        await bigWinPresentation.show(bigWinTier, totalWin, gameRoot, designW, designH);
        particleEffects.stopAll();
        await coinShowerPromise;
      }
    }

    // Track whether any feature triggered (to decide IDLE transition)
    let anyFeatureTriggered = false;

    // Helper: ensure we are in FEATURE state before running a sub-feature
    const ensureFeatureState = () => {
      if (stateMachine.state !== GameState.FEATURE) {
        stateMachine.transition(GameState.FEATURE);
      }
    };

    // === (2) SCATTER WINS + FREE GAME TRIGGER (flag only — runs last) ===
    const freeGamesTriggered = freeGamesFeature.canTrigger(scatterCount);
    if (freeGamesTriggered) {
      audioManager.playFreeSpinTrigger();
      logger.info(`Free games will trigger: ${scatterCount} scatters`);
    }

    // === (3) RUSH FEVER (3+ Rush symbols) ===
    const forceRF = QAController.isEnabled() && qaController.shouldForceFeature('rushFever');
    const qaRushCount = forceRF ? (qaController.override.rushCount || 3) : rushCount;
    if (forceRF || rushFeverFeature.canTrigger(rushCount)) {
      ensureFeatureState();
      anyFeatureTriggered = true;
      statusMessageText.text = 'RUSH FEVER!';
      const rushPrize = await presentRushFever(forceRF ? qaRushCount : rushCount, totalBet, false);
      if (rushPrize > 0) {
        statusMessageText.text = `RUSH FEVER PAYS $${rushPrize.toFixed(2)}`;
      }
      logger.info(`Rush Fever base game prize: $${rushPrize.toFixed(4)}`);
    }

    // === (4) JACKPOT PICK (random on WILD) ===
    const hasWild = WinEvaluator.hasWild(grid);
    const forceJP = QAController.isEnabled() && qaController.shouldForceFeature('jackpotPick');
    if (forceJP || (hasWild && JackpotPickFeature.shouldTrigger(true, false))) {
      ensureFeatureState();
      anyFeatureTriggered = true;
      audioManager.crossfadeTo('music_jackpot');
      const jpPrize = await jackpotPickFeature.start(gameRoot, designW, designH, totalBet);
      audioManager.crossfadeTo('music_main');
      if (jpPrize > 0) {
        balanceManager.addWin(jpPrize);
        hud.setWinLabel(`JACKPOT PICK WIN: $${jpPrize.toFixed(2)}`);
        logger.info(`Jackpot Pick base game prize: $${jpPrize.toFixed(4)}`);
      }
    }

    // === (4b) BIG WIN OVERRIDE ===
    if (QAController.isEnabled() && qaController.shouldForceFeature('bigWin')) {
      const bwTier = qaController.override.bigWinTier || 'amazing';
      const bwMult = bwTier === 'incredible' ? 55 : bwTier === 'awesome' ? 30 : 12;
      // Override totalWin to trigger big win presentation
      const forcedWin = totalBet * bwMult;
      balanceManager.addWin(forcedWin);
      hud.setWinLabel(`$${forcedWin.toFixed(2)}`);
      await bigWinPresentation.present(gameRoot, designW, designH, forcedWin, totalBet);
    }

    // === (5) FREE GAMES (runs after other base-spin features) ===
    const forceFG = QAController.isEnabled() && qaController.shouldForceFeature('freeGames');
    if (forceFG || freeGamesTriggered) {
      ensureFeatureState();
      anyFeatureTriggered = true;
      stateMachine.events.emit('feature:start', { type: 'FREE_GAMES' });
      logger.info(`FREE GAMES TRIGGERED! ${scatterCount} scatters`);

      const freeGameWin = await runFreeGames(totalBet);

      stateMachine.events.emit('feature:complete', { type: 'FREE_GAMES', award: freeGameWin });
    }

    // === QA: Record spin stats and consume overrides ===
    if (QAController.isEnabled()) {
      const qaFeatures: string[] = [];
      if (forceJP || (hasWild && anyFeatureTriggered)) qaFeatures.push('jackpotPick');
      if (forceRF || rushFeverFeature.canTrigger(rushCount)) qaFeatures.push('rushFever');
      if (forceFG || freeGamesTriggered) qaFeatures.push('freeGames');
      qaController.recordSpin({ totalBet, totalWin, features: qaFeatures, timestamp: Date.now() });
      qaController.consumeFeatureOverride();
    }

    // === RETURN TO IDLE ===
    if (!anyFeatureTriggered && totalWin <= 0) {
      hud.clearWin();
    }
    // Transition to IDLE from whatever state we are in (EVALUATING, PRESENTING_WINS, or FEATURE)
    if (stateMachine.state !== GameState.IDLE) {
      stateMachine.transition(GameState.IDLE);
    }

    // === UI STATE: IDLE ===
    spinInProgress = false;

    // Update status message based on outcome
    if (totalWin > 0) {
      statusMessageText.text = `WIN $${totalWin.toFixed(2)}`;
    } else {
      statusMessageText.text = 'PRESS SPIN TO START GAME';
    }

    if (balanceManager.canAffordBet()) {
      spinButton.setState(SpinButtonState.IDLE);
    } else {
      spinButton.setState(SpinButtonState.DISABLED);
    }
    betSelector.setEnabled(true);

    // Re-enable buy feature button if affordable
    const buyCostEnd = balanceManager.totalBet * BUY_FEATURE_COST_MULTIPLIER;
    buyFeatureButton.setEnabled(balanceManager.balance >= buyCostEnd);

    // Check autoplay stop conditions
    if (autoplayConfig) {
      let shouldStop = false;

      if (autoplayConfig.stopOnAnyWin && totalWin > 0) {
        logger.info('Autoplay stopped: win occurred');
        shouldStop = true;
      }
      if (autoplayConfig.stopOnFeature && (scatterCount >= 3 || rushCount >= 3)) {
        logger.info('Autoplay stopped: feature triggered');
        shouldStop = true;
      }

      if (!shouldStop && autoplayRemaining !== -1) {
        autoplayRemaining--;
        autoplayPanel.updateRemaining(autoplayRemaining);
        if (autoplayRemaining <= 0) {
          shouldStop = true;
          logger.info('Autoplay complete: all spins used');
        }
      }

      if (shouldStop) {
        stopAutoplay();
      } else {
        // Continue autoplay with a short delay
        setTimeout(() => doSpin(), turboMode ? 200 : 500);
      }
    }
  };

  /**
   * Run the Free Games bonus feature.
   * Automatically executes 12 free spins with escalating wilds,
   * updating the UI between each spin.
   */
  const runFreeGames = async (totalBet: number): Promise<number> => {
    logger.info('=== FREE GAMES START ===');

    // Crossfade to free game music
    audioManager.crossfadeTo('music_free');

    // Play free spin trigger sound
    audioManager.playFreeSpinTrigger();

    // Disable bet selector and spin button during free games
    spinButton.setState(SpinButtonState.DISABLED);
    betSelector.setEnabled(false);

    // Switch reels to free game mode (5 reels, no cards)
    reelSet.setMode(false);

    // Initialize feature
    freeGamesFeature.start();

    // Show free spin counter
    updateFreeSpinCounter(0, freeGamesFeature.totalSpins, true);

    // Brief pause before starting free spins
    await delay(1500);

    // Run each free spin
    while (!freeGamesFeature.isComplete()) {
      const spinNumber = freeGamesFeature.currentSpin + 1;
      updateFreeSpinCounter(spinNumber, freeGamesFeature.totalSpins, true);

      hud.clearWin();

      // Generate the free spin result (with escalating wilds applied to grid)
      const freeSpinResult = freeGamesFeature.doFreeSpin(totalBet);
      const { spinResult, spinWin, runningTotal } = freeSpinResult;

      // Animate reels
      reelSet.startSpin();

      // Use only the first 5 stop positions for free game (5 reels)
      const fgStopPositions = spinResult.stopPositions.slice(0, FREE_GAME_REEL_COUNT);
      await reelSet.stopReels(fgStopPositions);

      // Log the free spin grid
      const fgGrid = reelSet.getGrid();
      logger.info(`Free Spin ${freeSpinResult.spinNumber} grid:`);
      for (let row = 0; row < GameConfig.VISIBLE_ROWS; row++) {
        const rowSymbols = fgGrid.map((reel) => reel[row]);
        logger.info(`  Row ${row}: ${rowSymbols.join(' | ')}`);
      }

      // Show win if any
      if (spinWin > 0) {
        balanceManager.addWin(spinWin);

        const winMultiple = spinWin / totalBet;
        if (winMultiple >= GameConfig.BIG_WIN_INCREDIBLE) {
          hud.setWinLabel(`INCREDIBLE WIN: $${spinWin.toFixed(2)}`);
        } else if (winMultiple >= GameConfig.BIG_WIN_AWESOME) {
          hud.setWinLabel(`AWESOME WIN: $${spinWin.toFixed(2)}`);
        } else if (winMultiple >= GameConfig.BIG_WIN_AMAZING) {
          hud.setWinLabel(`AMAZING WIN: $${spinWin.toFixed(2)}`);
        } else {
          hud.setWin(spinWin);
        }

        // Present win animations
        if (spinResult.wins.length > 0) {
          // Sparkle on winning symbols during free games
          for (const win of spinResult.wins) {
            for (const [reelIdx, row] of win.positions) {
              const symX = reelSet.x + reelIdx * GameConfig.SYMBOL_WIDTH + GameConfig.SYMBOL_WIDTH / 2;
              const symY = reelSet.y + row * GameConfig.SYMBOL_HEIGHT + GameConfig.SYMBOL_HEIGHT / 2;
              particleEffects.sparkleAt(gameRoot, symX, symY);
            }
          }
          await winLineAnimation.presentWins(spinResult.wins, totalBet);
        }

        // Check for Big Win celebration during free games
        const fgBigWinTier = BigWinPresentation.getTier(spinWin, totalBet);
        if (fgBigWinTier !== BigWinTier.NONE) {
          logger.info(`Free Game Big Win: tier=${fgBigWinTier}, ${(spinWin / totalBet).toFixed(1)}x bet`);
          const fgCoinPromise = particleEffects.coinShower(gameRoot, designW, designH);
          await bigWinPresentation.show(fgBigWinTier, spinWin, gameRoot, designW, designH);
          particleEffects.stopAll();
          await fgCoinPromise;
        }
      }

      // Check for Rush Fever during free games (with 2x multiplier)
      const fgRushCount = WinEvaluator.countRush(fgGrid);
      if (rushFeverFeature.canTrigger(fgRushCount)) {
        const rushPrize = await presentRushFever(fgRushCount, totalBet, true);
        logger.info(`Rush Fever during free game spin ${freeSpinResult.spinNumber}: $${rushPrize.toFixed(4)}`);
      }

      // Check for Jackpot Pick during free games (requires WILD on grid)
      const fgHasWild = WinEvaluator.hasWild(fgGrid);
      if (fgHasWild && JackpotPickFeature.shouldTrigger(true, true, freeSpinResult.spinNumber)) {
        audioManager.crossfadeTo('music_jackpot');
        const jpPrize = await jackpotPickFeature.start(gameRoot, designW, designH, totalBet);
        audioManager.crossfadeTo('music_free');
        if (jpPrize > 0) {
          balanceManager.addWin(jpPrize);
          hud.setWinLabel(`JACKPOT PICK WIN: $${jpPrize.toFixed(2)}`);
          logger.info(`Jackpot Pick during free game spin ${freeSpinResult.spinNumber}: $${jpPrize.toFixed(4)}`);
        }
      }

      // Update counter after spin completes
      updateFreeSpinCounter(freeSpinResult.spinNumber, freeGamesFeature.totalSpins, true);

      logger.info(
        `Free Spin ${freeSpinResult.spinNumber} complete: ` +
        `win=$${spinWin.toFixed(4)}, running=$${runningTotal.toFixed(4)}`
      );

      // Brief delay between free spins
      if (!freeGamesFeature.isComplete()) {
        await delay(800);
      }
    }

    // End free games
    const totalFreeGameWin = freeGamesFeature.end();

    // Play free game end sound
    audioManager.playFreeGameEnd();

    // Show total free game winnings
    if (totalFreeGameWin > 0) {
      hud.setWinLabel(`FREE GAMES TOTAL WIN: $${totalFreeGameWin.toFixed(2)}`);
    } else {
      hud.setWinLabel('FREE GAMES COMPLETE');
    }

    logger.info(`=== FREE GAMES END === Total win: $${totalFreeGameWin.toFixed(4)}`);

    // Crossfade back to main game music
    audioManager.crossfadeTo('music_main');

    // Pause to show total win
    await delay(3000);

    // Switch back to base game mode (6 reels)
    reelSet.setMode(true);

    // Hide free spin counter
    updateFreeSpinCounter(0, 0, false);

    // Reset feature state
    freeGamesFeature.reset();
    hud.clearWin();

    return totalFreeGameWin;
  };

  /** Simple delay helper */
  const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const stopAutoplay = () => {
    autoplayConfig = null;
    autoplayRemaining = 0;
    autoplayPanel.setRunning(false);
    logger.info('Autoplay stopped');
  };

  // 15. Wire spin button
  spinButton.onSpin(() => {
    doSpin();
  });

  // Long-press on spin button toggles bet selector overlay (800ms hold)
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  let longPressTriggered = false;
  spinButton.on('pointerdown', () => {
    longPressTriggered = false;
    longPressTimer = setTimeout(() => {
      longPressTriggered = true;
      betSelector.visible = !betSelector.visible;
      logger.info(`Bet selector toggled via long-press: visible=${betSelector.visible}`);
    }, 800);
  });
  const clearLongPress = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };
  spinButton.on('pointerup', clearLongPress);
  spinButton.on('pointerupoutside', clearLongPress);

  // 16. Wire autoplay
  autoplayPanel.onStartAutoplay((config) => {
    autoplayConfig = config;
    autoplayRemaining = config.spinCount;
    logger.info(`Autoplay started: ${config.spinCount} spins`);
    // Trigger the first spin
    doSpin();
  });

  autoplayPanel.onStopAutoplay(() => {
    stopAutoplay();
  });

  // 17. Wire buy feature button
  buyFeatureButton.onBuy(() => {
    if (!stateMachine.isIdle || spinInProgress) {
      logger.warn('Buy Feature: game not idle');
      return;
    }

    const cost = buyFeatureButton.getCost();
    const totalBet = balanceManager.totalBet;

    if (balanceManager.balance < cost) {
      logger.warn('Buy Feature: insufficient balance');
      return;
    }

    logger.info(`Buy Feature activated: cost=$${cost.toFixed(2)}`);

    // Deduct cost
    balanceManager.deductAmount(cost);

    // Stop any autoplay
    if (autoplayConfig) {
      stopAutoplay();
    }

    // Disable UI during feature
    spinInProgress = true;
    spinButton.setState(SpinButtonState.DISABLED);
    betSelector.setEnabled(false);
    buyFeatureButton.setEnabled(false);

    // Trigger free games directly
    stateMachine.transition(GameState.SPINNING);
    stateMachine.transition(GameState.EVALUATING);
    stateMachine.transition(GameState.FEATURE);
    stateMachine.events.emit('feature:start', { type: 'FREE_GAMES' });
    logger.info('FREE GAMES TRIGGERED via Buy Feature!');

    runFreeGames(totalBet).then((freeGameWin) => {
      stateMachine.events.emit('feature:complete', { type: 'FREE_GAMES', award: freeGameWin });
      stateMachine.transition(GameState.IDLE);

      spinInProgress = false;
      if (balanceManager.canAffordBet()) {
        spinButton.setState(SpinButtonState.IDLE);
      } else {
        spinButton.setState(SpinButtonState.DISABLED);
      }
      betSelector.setEnabled(true);

      const buyCost = balanceManager.totalBet * BUY_FEATURE_COST_MULTIPLIER;
      buyFeatureButton.setEnabled(balanceManager.balance >= buyCost);
    });
  });

  setProgress(1.0);

  // 17. Add game ticker for reel updates
  app.ticker.add((ticker) => {
    reelSet.update(ticker.deltaTime);
  });

  // 18. Tap on reels area to spin (touch gesture support for mobile)
  // Create an invisible interactive overlay on the reel area
  {
    const reelTapArea = new Graphics();
    reelTapArea.rect(0, 0, reelTotalW, reelTotalH);
    reelTapArea.fill({ color: 0x000000, alpha: 0.001 }); // nearly invisible but interactive
    reelTapArea.x = 0;
    reelTapArea.y = 0;
    reelTapArea.eventMode = 'static';
    reelTapArea.cursor = 'pointer';
    reelSet.addChild(reelTapArea);

    reelTapArea.on('pointerdown', () => {
      // Only allow tap-to-spin when idle and not in a feature/animation
      if (!spinInProgress && stateMachine.isIdle && !winLineAnimation.isPresenting) {
        spinButton.triggerSpin();
      } else if (winLineAnimation.isPresenting) {
        // Tap to skip win presentation
        winLineAnimation.skip();
      }
    });

    logger.info('Tap-on-reels to spin enabled');
  }

  // Hide preloader
  hidePreloader();

  // === Log a test spin to verify the evaluator ===
  logger.info('=== TEST SPIN (verification) ===');
  const testResult = spinGenerator.generate(BASE_GAME_STRIPS, {
    isBaseGame: true,
    totalBet: 2.50,
  });
  logger.info('Test grid:');
  for (let row = 0; row < 3; row++) {
    const rowSymbols = testResult.grid.map((reel) => reel[row]);
    logger.info(`  Row ${row}: ${rowSymbols.join(' | ')}`);
  }
  logger.info(`Test wins: ${testResult.wins.length}, totalWin=$${testResult.totalWin.toFixed(4)}`);
  for (const w of testResult.wins) {
    logger.info(`  ${w.symbol} x${w.streakLength}: ${w.ways} ways, ${w.coins} coins`);
  }
  logger.info('=== END TEST SPIN ===');

  logger.info('Rush Fever 7s Deluxe initialized successfully');
  logger.info(`Renderer: ${app.renderer.width}x${app.renderer.height}`);
  logger.info(`Design: ${designW}x${designH}`);
}

init().catch((err) => {
  console.error('Failed to initialize game:', err);
  const logger2 = new Logger('main');
  logger2.error(`Init failed: ${err}`);
});
